import apiClient from './apiClient';
import type { TestCatalogItem, CreateTestCatalogItemRequest } from '../types/testCatalogItem';
import type {
  ProposalMethodologyBlock,
  UpsertMethodologyBlockRequest,
} from '../types/schedule';

export const getTestCatalogItems = async (): Promise<TestCatalogItem[]> => {
  const response = await apiClient.get<TestCatalogItem[]>('/test-catalog');
  return response.data;
};

export const getTestCatalogItem = async (key: string): Promise<TestCatalogItem> => {
  const response = await apiClient.get<TestCatalogItem>(`/test-catalog/${key}`);
  return response.data;
};

export const searchTestCatalog = async (query: string): Promise<TestCatalogItem[]> => {
  const response = await apiClient.get<TestCatalogItem[]>('/test-catalog/search', { params: { q: query } });
  return response.data;
};

export const createTestCatalogItem = async (data: CreateTestCatalogItemRequest): Promise<TestCatalogItem> => {
  const response = await apiClient.post<TestCatalogItem>('/test-catalog', data);
  return response.data;
};

export const updateTestCatalogItem = async (key: string, data: Partial<TestCatalogItem>): Promise<TestCatalogItem> => {
  const response = await apiClient.put<TestCatalogItem>(`/test-catalog/${key}`, data);
  return response.data;
};

export const deleteTestCatalogItem = async (key: string): Promise<void> => {
  await apiClient.delete(`/test-catalog/${key}`);
};

export const importTestCatalog = async (items: CreateTestCatalogItemRequest[]): Promise<{ created: number }> => {
  const response = await apiClient.post<{ created: number }>('/test-catalog/import', { items });
  return response.data;
};

/**
 * REQ-06 (round 2c): capable-machine map for ALL tests.
 *
 * GET /api/test-catalog/capable-machines
 * Returns an OBJECT (map) keyed by lower-cased TestCatalogItemKey GUID, valued
 * by an array of lower-cased MachineKey GUIDs that are capable of running the
 * test (UNION across the test's task types, intersected with ACTIVE machines).
 *
 * IMPORTANT: a test whose key is ABSENT from the map has an EMPTY capable set —
 * never hide such a test. Callers should read `map[testKey.toLowerCase()] ?? []`.
 * All GUIDs are lower-cased server-side; compare case-insensitively to be safe.
 */
export const fetchCapableMachines = async (): Promise<Record<string, string[]>> => {
  const response = await apiClient.get<Record<string, string[]>>('/test-catalog/capable-machines');
  return response.data ?? {};
};

// ── Phase 5, Item 6 — Methodology block sub-resource ──────────────────────

/**
 * GET /api/test-catalog/:key/methodology
 * Returns the active methodology block for a catalog item, or null if none exists.
 */
export const getMethodologyBlock = async (
  testCatalogItemKey: string,
): Promise<ProposalMethodologyBlock | null> => {
  const response = await apiClient.get<ProposalMethodologyBlock | null>(
    `/test-catalog/${testCatalogItemKey}/methodology`,
  );
  return response.data ?? null;
};

/**
 * POST /api/test-catalog/:key/methodology
 * Upsert: creates a new block or replaces the current one (soft-deletes prior version).
 */
export const upsertMethodologyBlock = async (
  testCatalogItemKey: string,
  data: UpsertMethodologyBlockRequest,
): Promise<ProposalMethodologyBlock> => {
  const response = await apiClient.post<ProposalMethodologyBlock>(
    `/test-catalog/${testCatalogItemKey}/methodology`,
    data,
  );
  return response.data;
};

/**
 * DELETE /api/test-catalog/:key/methodology
 * Soft-deletes the currently active methodology block.
 */
export const deleteMethodologyBlock = async (
  testCatalogItemKey: string,
): Promise<void> => {
  await apiClient.delete(`/test-catalog/${testCatalogItemKey}/methodology`);
};
